<template>
  <div class="performance">
    <LoaderIndicator
      v-if="shouldRenderLoaderIndicator"
      class="performance__loader-indicator"
      size="36"
    />
    <HelperViewStatus
      v-else-if="shouldRenderErrorState"
      class="performance__helper"
      generic-error
      button-label="Tentar novamente"
      @click="handleOnRetry"
    />
    <DataTable
      v-else-if="hasData"
      data-testid="performance-table"
      class="performance__table"
      :columns="columns"
      :data="data"
      :status="status"
      :has-interaction="false"
    />
    <HelperViewContextual
      v-else
      class="performance__helper"
      icon="ED0030"
      description="Você ainda não possui dados de performance"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import {
  DataTable,
  LoaderIndicator,
  HelperViewContextual,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { MoneyCell } from '@/modules/common/components/MoneyCell';
import { PercentualCell } from '@/modules/common/components/PercentualCell';

import {
  PortfolioPerformancePeriod,
  PortfolioPerformanceResponse
} from '@/modules/wealth/services/portfolio';
import { PerformanceFetchingStatus } from '../../types';

interface Props {
  performanceData?: PortfolioPerformanceResponse | null;
  status: PerformanceFetchingStatus;
}
const props = defineProps<Props>();

interface Emit {
  (e: 'on-retry'): void;
}
const emit = defineEmits<Emit>();

const shouldRenderErrorState = computed(() => {
  return props.status === PerformanceFetchingStatus.Error;
});

const shouldRenderLoaderIndicator = computed(() => {
  return (
    props.status === PerformanceFetchingStatus.Loading && !props.performanceData
  );
});

const defaultColumns = [
  {
    label: 'Período',
    getter: (period: PortfolioPerformancePeriod) => period.date.long
  },
  {
    label: 'Rendimento',
    align: 'right',
    component: MoneyCell,
    getter: (period: PortfolioPerformancePeriod) => ({
      text: period.profitValue.formatted,
      align: 'right',
      width: '100%',
      enableToggleMoney: true
    })
  },
  {
    label: 'Rentabilidade',
    align: 'right',
    component: PercentualCell,
    getter: (period: PortfolioPerformancePeriod) => ({
      percentualValue: period.return.value,
      percentualFormatted: period.return.formatted,
      align: 'right',
      width: '100%'
    })
  }
];

const hasData = computed(() => {
  return data.value.length > 0;
});

const data = computed(() => {
  return props.performanceData?.periods || [];
});

const columns = computed(() => {
  return defaultColumns.concat(benchmarksColumns.value);
});

const benchmarksColumns = computed(() => {
  const benchmarks = props.performanceData?.periods?.[0].benchmarks || [];

  return benchmarks.map((benchmark, index) => ({
    label: benchmark.label,
    align: 'right',
    component: PercentualCell,
    getter: (period: PortfolioPerformancePeriod) => ({
      percentualValue: period.benchmarks[index].percentage.value,
      percentualFormatted: period.benchmarks[index].percentage.formatted,
      align: 'right',
      width: '100%'
    })
  }));
});

const handleOnRetry = () => {
  emit('on-retry');
};
</script>

<style lang="less" scoped>
.performance {
  width: 100%;

  &__loader-indicator {
    display: flex;
    justify-content: center;
    margin-top: @size-spacing-x1000 !important;
  }

  &__helper {
    margin: @size-spacing-x1000 auto 0 auto !important;
  }

  &__table {
    padding: @size-spacing-x500;
    border: @size-border-x400 solid @divider-primary;
    border-radius: @size-radius-x200;

    .deep(th) {
      white-space: nowrap;
    }

    .deep(td) {
      &:not(:first-child) {
        white-space: nowrap;
      }
    }
  }
}
</style>
